import { Controller } from "@hotwired/stimulus"

// Recalculates the displayed outstanding balance whenever the user edits the
// starting balance input. The "effect" value is the (cached) sum of document
// effects = current_outstanding_balance - current_starting_balance, so the new
// outstanding balance is simply: parsed(starting) + effect.
//
// Also updates the balance breakdown table cells (balanceCell targets) and the
// export link (exportLink target) when present.
export default class extends Controller {
  static targets = ["startingBalance", "output", "balanceCell", "exportLink"]
  static values = { effect: Number }

  connect() {
    if (this.hasBalanceCellTarget) {
      this.updateBreakdownCells(this.parsedStartingBalance)
    }
  }

  selectIfZero({ target }) {
    const raw = (target.value || "0").replace(/,/g, "").trim()
    const num = parseFloat(raw)
    if (num === 0) target.select()
  }

  get parsedStartingBalance() {
    const raw = (this.startingBalanceTarget.value || "0").toString().replace(/,/g, "").trim()
    const val = parseFloat(raw)
    return isNaN(val) ? 0 : val
  }

  update() {
    const startingNum = this.parsedStartingBalance
    const total = startingNum + (this.effectValue || 0)

    const formatted = this.formatMoney(total)
    const cssClass = total < 0 ? "text-success" : (total > 0 ? "text-danger" : "")
    this.outputTarget.innerHTML = `<span class="${cssClass}"><span dir="ltr">₪ ${formatted}</span></span>`

    this.updateBreakdownCells(startingNum)
    this.updateExportLink(startingNum)
  }

  updateBreakdownCells(startingNum) {
    if (!this.hasBalanceCellTarget) return

    this.balanceCellTargets.forEach(cell => {
      const cumulativeEffect = parseFloat(cell.dataset.cumulativeEffect) || 0
      const balance = startingNum + cumulativeEffect
      const cssClass = balance < 0 ? "text-success" : (balance > 0 ? "text-danger" : "")

      // Clear existing color classes on the cell itself (for the final row)
      cell.classList.remove("text-success", "text-danger")
      if (cssClass) cell.classList.add(cssClass.replace("text-", "text-"))

      const span = cell.querySelector("span[dir='ltr']")
      if (span) {
        span.textContent = `₪ ${this.formatMoney(balance)}`
      }
    })
  }

  updateExportLink(startingNum) {
    if (!this.hasExportLinkTarget) return

    const link = this.exportLinkTarget
    const url = new URL(link.href)
    url.searchParams.set("starting_balance", startingNum)
    link.href = url.toString()
  }

  formatMoney(value) {
    const isWhole = value === Math.trunc(value)
    return value.toLocaleString("en-US", {
      minimumFractionDigits: isWhole ? 0 : 2,
      maximumFractionDigits: 2
    })
  }
}
